/**
 * §58 PR 4 — hooks for putting a hauler on EXPRESS with its one customer.
 *
 *   PUT /v1/platform/haulers/:tenantId/express-customer
 *   GET /v1/platform/haulers/:tenantId/express-customer/preview?operatorId=
 *
 * Kept apart from hooks.ts so the §58 PR 2 Plan & Features tab (which owns that file's plan
 * hooks) and this dialog merge cleanly in either order.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import { HAULER_PLAN_KEY } from './plan-hooks';

export interface ExpressPreview {
  readonly operator: { readonly id: string; readonly name: string };
  /** The hauler's other active customers: frozen for new work, never deactivated. */
  readonly otherCustomers: number;
  /** Their open jobs, which finish normally. */
  readonly openJobs: number;
  readonly hasCustomerRow: boolean;
  /** No customer row yet: the 2–4 letter abbreviation is needed to create it. */
  readonly abbreviationRequired: boolean;
}

export interface SetExpressBody {
  readonly operatorId: string;
  readonly abbreviation?: string;
  readonly reason: string;
  readonly clearOverrides?: boolean;
}

export interface SetExpressResponse {
  readonly template: 'EXPRESS';
  readonly expressOperatorId: string;
  readonly expressOperatorName: string;
  readonly customerCreated: boolean;
}

export function useExpressPreview(
  tenantId: string,
  operatorId: string | null,
): ReturnType<typeof useApiQuery<ExpressPreview>> {
  const opts = useApiOpts();
  return useApiQuery<ExpressPreview>(
    opts,
    `/v1/platform/haulers/${tenantId}/express-customer/preview?operatorId=${encodeURIComponent(operatorId ?? '')}`,
    {
      queryKey: ['hauler-express-preview', tenantId, operatorId],
      enabled: operatorId !== null,
      staleTime: 0,
    },
  );
}

export function useSetExpressCustomer(
  tenantId: string,
  onDone: (r: SetExpressResponse) => void,
): ReturnType<typeof useApiMutation<SetExpressBody, SetExpressResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<SetExpressBody, SetExpressResponse>(
    opts,
    `/v1/platform/haulers/${tenantId}/express-customer`,
    'PUT',
    {
      onSuccess: (r) => {
        // The answer is not a plan view: refetch the Plan & Features tab's plan.
        void qc.invalidateQueries({ queryKey: HAULER_PLAN_KEY(tenantId) });
        void qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
        onDone(r);
      },
    },
  );
}
